/**
 * Room Programs — scheduled, host-defined show programs.
 *
 * A host defines a program for a room: a name, one to six topics, and a
 * schedule. The signaling producer runs it on that schedule (or on demand):
 * it collects fresh sources for every topic, writes a `TownhallProgramManifest`
 * in the same shape the Morning Rundown producer emits, and stores the result
 * as a run. In the room the host loads any previous run from the Program panel
 * (Mode → Program) instead of uploading a file.
 *
 * This generalises `~/agents/openclaw/skills/morning-show`: the six fixed beats
 * become the program's topics, `beats.json` becomes a row, and the OpenClaw
 * cron becomes the in-process scheduler in signaling.
 *
 * Wire types and pure helpers only. Storage lives in `@telegamez/db`
 * (`room-programs.ts`); generation lives in signaling (`libs/programs/`).
 */

import type { ProgramManifestSummary, TownhallProgramManifest } from './TownhallProgramManifest';
import { resolveRoomTimeZone } from './RoomTime';

// ─── Vocabulary ─────────────────────────────────────────────────────────────

export const ROOM_PROGRAM_MIN_TOPICS = 1;
export const ROOM_PROGRAM_MAX_TOPICS = 6;

export const ROOM_PROGRAM_CADENCES = ['daily', 'weekdays', 'weekly', 'manual'] as const;
export type RoomProgramCadence = typeof ROOM_PROGRAM_CADENCES[number];

export const ROOM_PROGRAM_CADENCE_LABELS: Record<RoomProgramCadence, string> = {
    daily: 'Every day',
    weekdays: 'Weekdays',
    weekly: 'Once a week',
    manual: 'Manual only',
};

export const ROOM_PROGRAM_RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed'] as const;
export type RoomProgramRunStatus = typeof ROOM_PROGRAM_RUN_STATUSES[number];

export const ROOM_PROGRAM_RUN_TRIGGERS = ['schedule', 'manual'] as const;
export type RoomProgramRunTrigger = typeof ROOM_PROGRAM_RUN_TRIGGERS[number];

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export const ROOM_PROGRAM_LIMITS = {
    maxProgramsPerRoom: 10,
    maxNameChars: 120,
    maxThemeChars: 120,
    maxTopicLabelChars: 80,
    maxSearchHintChars: 200,
    maxAngleChars: 400,
    maxHostInstructionsChars: 1500,
    /** Pasted brief a host can build a one-off show from. */
    maxBriefChars: 20000,
    minBriefChars: 40,
    maxRunsListed: 50,
    /** A run still `running` after this long is presumed orphaned by a crashed producer. */
    runStaleAfterMs: 30 * 60 * 1000,
} as const;

// ─── Definition ─────────────────────────────────────────────────────────────

export interface RoomProgramTopic {
    /** Segment name as it appears in the agenda, e.g. "Tech & AI". */
    label: string;
    /** Search terms the collector uses for this segment. Defaults to the label. */
    searchHint?: string;
    /**
     * Editorial angle the producer writes the segment from — the generalised
     * form of the Morning Rundown politics charter. Free text; empty means
     * "straight, well-sourced coverage with a take per story".
     */
    angle?: string;
}

export interface RoomProgramSchedule {
    cadence: RoomProgramCadence;
    /** Local wall-clock time, `HH:MM` (24-hour), in `timezone`. */
    runAt: string;
    /** IANA zone the time is read in. Falls back to the room's zone at creation. */
    timezone: string;
    /** 0 (Sunday) – 6 (Saturday). Only read for `weekly`. */
    weekday?: number;
}

export interface RoomProgram {
    id: string;
    roomId: string;
    name: string;
    /** Catalog theme, e.g. "Daily Morning Show". Also written to `metadata.theme`. */
    showTheme: string | null;
    topics: RoomProgramTopic[];
    schedule: RoomProgramSchedule;
    /** Delivery directives copied into `companionBriefing.hostInstructions`. */
    hostInstructions: string | null;
    enabled: boolean;
    lastRunAt: string | null;
    nextRunAt: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Create / update body. Everything the host edits; nothing the server owns. */
export interface RoomProgramInput {
    name: string;
    showTheme?: string | null;
    topics: RoomProgramTopic[];
    schedule: RoomProgramSchedule;
    hostInstructions?: string | null;
    enabled?: boolean;
}

// ─── Runs ───────────────────────────────────────────────────────────────────

export interface RoomProgramRun {
    id: string;
    programId: string;
    roomId: string;
    status: RoomProgramRunStatus;
    triggeredBy: RoomProgramRunTrigger;
    /** `metadata.title` of the produced manifest; null until a run succeeds. */
    title: string | null;
    summary: ProgramManifestSummary | null;
    error: string | null;
    /** First characters of the pasted brief this run was planned from; null for topic-based runs. */
    briefPreview?: string | null;
    /** Pack items the producer had to work from. Diagnostic. */
    sourceCount: number | null;
    startedAt: string | null;
    finishedAt: string | null;
    createdAt: string;
    /** Program name at listing time, for display. */
    programName?: string | null;
}

/** A run with its manifest — what the room applies or the host downloads. */
export interface RoomProgramRunDetail extends RoomProgramRun {
    manifest: TownhallProgramManifest | null;
}

// ─── Validation ─────────────────────────────────────────────────────────────

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isValidRunAt(value: unknown): value is string {
    return typeof value === 'string' && TIME_RE.test(value);
}

function text(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

/**
 * Validate a create/update body from the dashboard. Returns the normalised
 * value on success; on failure `errors` names every problem so the form can
 * show them all at once rather than one per round trip.
 */
export function validateRoomProgramInput(raw: unknown): {
    valid: boolean;
    errors: string[];
    value: RoomProgramInput | null;
} {
    const errors: string[] = [];
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { valid: false, errors: ['Program must be a JSON object'], value: null };
    }
    const input = raw as Record<string, unknown>;

    const name = text(input.name);
    if (!name) errors.push('name is required');
    else if (name.length > ROOM_PROGRAM_LIMITS.maxNameChars) errors.push(`name exceeds ${ROOM_PROGRAM_LIMITS.maxNameChars} characters`);

    const showTheme = text(input.showTheme);
    if (showTheme.length > ROOM_PROGRAM_LIMITS.maxThemeChars) errors.push(`showTheme exceeds ${ROOM_PROGRAM_LIMITS.maxThemeChars} characters`);

    const hostInstructions = text(input.hostInstructions);
    if (hostInstructions.length > ROOM_PROGRAM_LIMITS.maxHostInstructionsChars) {
        errors.push(`hostInstructions exceeds ${ROOM_PROGRAM_LIMITS.maxHostInstructionsChars} characters`);
    }

    const topics: RoomProgramTopic[] = [];
    if (!Array.isArray(input.topics)) {
        errors.push('topics must be an array');
    } else {
        if (input.topics.length < ROOM_PROGRAM_MIN_TOPICS || input.topics.length > ROOM_PROGRAM_MAX_TOPICS) {
            errors.push(`topics must contain between ${ROOM_PROGRAM_MIN_TOPICS} and ${ROOM_PROGRAM_MAX_TOPICS} entries`);
        }
        const seen = new Set<string>();
        input.topics.forEach((rawTopic, index) => {
            if (!rawTopic || typeof rawTopic !== 'object') {
                errors.push(`topics[${index}] must be an object`);
                return;
            }
            const topic = rawTopic as Record<string, unknown>;
            const label = text(topic.label);
            if (!label) errors.push(`topics[${index}].label is required`);
            else if (label.length > ROOM_PROGRAM_LIMITS.maxTopicLabelChars) {
                errors.push(`topics[${index}].label exceeds ${ROOM_PROGRAM_LIMITS.maxTopicLabelChars} characters`);
            }
            const key = label.toLowerCase();
            if (label && seen.has(key)) errors.push(`topics[${index}].label duplicates another topic`);
            seen.add(key);
            const searchHint = text(topic.searchHint);
            if (searchHint.length > ROOM_PROGRAM_LIMITS.maxSearchHintChars) {
                errors.push(`topics[${index}].searchHint exceeds ${ROOM_PROGRAM_LIMITS.maxSearchHintChars} characters`);
            }
            const angle = text(topic.angle);
            if (angle.length > ROOM_PROGRAM_LIMITS.maxAngleChars) {
                errors.push(`topics[${index}].angle exceeds ${ROOM_PROGRAM_LIMITS.maxAngleChars} characters`);
            }
            topics.push({
                label,
                ...(searchHint ? { searchHint } : {}),
                ...(angle ? { angle } : {}),
            });
        });
    }

    let schedule: RoomProgramSchedule | null = null;
    if (!input.schedule || typeof input.schedule !== 'object') {
        errors.push('schedule is required');
    } else {
        const rawSchedule = input.schedule as Record<string, unknown>;
        const cadence = rawSchedule.cadence;
        if (!ROOM_PROGRAM_CADENCES.includes(cadence as RoomProgramCadence)) {
            errors.push(`schedule.cadence must be one of ${ROOM_PROGRAM_CADENCES.join(', ')}`);
        }
        const runAt = isValidRunAt(rawSchedule.runAt) ? rawSchedule.runAt : null;
        if (!runAt) errors.push('schedule.runAt must be HH:MM (24-hour)');
        const timezone = text(rawSchedule.timezone);
        if (!timezone) errors.push('schedule.timezone is required');
        else if (resolveRoomTimeZone(timezone) !== timezone) errors.push(`schedule.timezone "${timezone}" is not a known IANA zone`);
        let weekday: number | undefined;
        if (rawSchedule.weekday !== undefined && rawSchedule.weekday !== null) {
            const parsed = Number(rawSchedule.weekday);
            if (!Number.isInteger(parsed) || parsed < 0 || parsed > 6) errors.push('schedule.weekday must be 0-6');
            else weekday = parsed;
        }
        if (cadence === 'weekly' && weekday === undefined) errors.push('schedule.weekday is required for a weekly cadence');
        if (errors.length === 0 && runAt) {
            schedule = {
                cadence: cadence as RoomProgramCadence,
                runAt,
                timezone,
                ...(weekday !== undefined ? { weekday } : {}),
            };
        }
    }

    if (errors.length > 0 || !schedule) {
        return { valid: false, errors, value: null };
    }

    return {
        valid: true,
        errors: [],
        value: {
            name,
            showTheme: showTheme || null,
            topics,
            schedule,
            hostInstructions: hostInstructions || null,
            enabled: input.enabled === undefined ? true : input.enabled === true,
        },
    };
}

// ─── Scheduling ─────────────────────────────────────────────────────────────

interface ZonedParts {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
}

function zonedParts(timeZone: string, at: Date): ZonedParts {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(at);
    const read = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
    // Some engines still print midnight as "24" even under h23.
    const hour = read('hour') % 24;
    return { year: read('year'), month: read('month'), day: read('day'), hour, minute: read('minute'), second: read('second') };
}

/** Milliseconds the zone is ahead of UTC at `at`. */
function zoneOffsetMs(timeZone: string, at: Date): number {
    const p = zonedParts(timeZone, at);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * The instant at which `timeZone` reads the given local wall-clock time.
 * Two offset passes settle DST edges; a wall time that does not exist (the
 * spring-forward gap) resolves to the instant one hour later, which is what a
 * host who scheduled 02:30 that night would expect.
 */
export function zonedTimeToUtc(
    timeZone: string,
    year: number,
    month: number,
    day: number,
    hour: number,
    minute: number,
): Date {
    const wall = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let guess = wall;
    for (let i = 0; i < 2; i++) {
        guess = wall - zoneOffsetMs(timeZone, new Date(guess));
    }
    return new Date(guess);
}

/**
 * The next instant the schedule fires strictly after `from`, or null for a
 * manual-only program. Searches two weeks of calendar days in the schedule's
 * zone — enough for any cadence here to find its next slot.
 */
export function computeNextRunAt(schedule: RoomProgramSchedule, from: Date = new Date()): Date | null {
    if (schedule.cadence === 'manual') return null;
    const match = TIME_RE.exec(schedule.runAt);
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const timeZone = resolveRoomTimeZone(schedule.timezone);
    const today = zonedParts(timeZone, from);
    const weekly = schedule.cadence === 'weekly' ? (schedule.weekday ?? 1) : null;

    for (let offset = 0; offset < 15; offset++) {
        const dayUtc = new Date(Date.UTC(today.year, today.month - 1, today.day + offset));
        const weekday = dayUtc.getUTCDay();
        if (schedule.cadence === 'weekdays' && (weekday === 0 || weekday === 6)) continue;
        if (weekly !== null && weekday !== weekly) continue;
        const instant = zonedTimeToUtc(
            timeZone,
            dayUtc.getUTCFullYear(),
            dayUtc.getUTCMonth() + 1,
            dayUtc.getUTCDate(),
            hour,
            minute,
        );
        if (instant.getTime() > from.getTime()) return instant;
    }
    return null;
}

/** Human-readable schedule for the dashboard and the room panel. */
export function describeRoomProgramSchedule(schedule: RoomProgramSchedule): string {
    if (schedule.cadence === 'manual') return 'Runs only when you press Run now';
    const when = `${schedule.runAt} ${schedule.timezone}`;
    switch (schedule.cadence) {
        case 'daily':
            return `Every day at ${when}`;
        case 'weekdays':
            return `Weekdays at ${when}`;
        case 'weekly':
            return `Every ${WEEKDAY_LABELS[schedule.weekday ?? 1]} at ${when}`;
    }
}
